import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase';

// PresenceChannel — Supabase Realtime presence for one room. Every
// joined user broadcasts a small payload (id, handle, avatar) to every
// other connected client so the floor can render real bodies for real
// people, not just AI agents and the booth DJ.
//
// One instance per room. Subscription opens at room boot so spectators
// see joined users; track() only fires once the local user actually
// steps onto the floor. The remote-user list returned to listeners
// always excludes self — main.ts owns its own User record.

export interface PresenceUser {
  userId: string;
  handle: string;
  avatar3dId?: string;
  /** DiceBear seed for the user's spectator bot. Carried over the wire
   *  so remote clients render the same robot face as the user sees on
   *  their own machine. See src/lib/bottAvatar.ts. */
  avatarSeed?: string;
  /** Index into the brand palette in src/lib/bottAvatar.ts. */
  avatarPaletteIndex?: number;
}

export type PresenceListener = (remoteUsers: PresenceUser[]) => void;

// Supabase keys presence state by the configured presence key (we use
// userId). Each value is an array of presence rows in case the same
// user has multiple tabs open. We collapse on userId so one human =
// one avatar regardless of tab count.
type PresenceRow = PresenceUser & { presence_ref?: string };

/** Heartbeat cadence (ms) at which we re-read the local presenceState()
 *  cache and emit to listeners. Defends against the case where Supabase's
 *  presence:join / presence:sync event is dropped (we've seen this in
 *  practice: User-A is in the room, User-B joins, and User-A's join event
 *  never arrives — but presenceState() *does* contain User-B once the
 *  state has propagated). The re-read is local (no network), so even a
 *  short interval is cheap. */
const PRESENCE_HEARTBEAT_MS = 3000;

/** Cadence (ms) at which a tracking client re-tracks itself. This is the
 *  defense against the worse case where the presenceState() cache itself
 *  never receives the missed join — re-tracking forces the server to
 *  re-broadcast our row to every subscriber, so subscribers that missed
 *  the original join catch up on the next ping. Slower than the local
 *  heartbeat because it generates network traffic per joined client. */
const PRESENCE_RETRACK_MS = 15000;

export class PresenceChannel {
  private roomId: string;
  /** Per-tab presence-channel key. Independent of auth so the channel
   *  can subscribe before sign-in (spectators see joined users). */
  private clientKey: string;
  /** Auth user id of the local user. Used to filter self out of the
   *  remote snapshot. Updated as auth state evolves. */
  private selfUserId: string | null = null;
  private channel: RealtimeChannel | null = null;
  private listeners = new Set<PresenceListener>();
  private subscribed = false;
  /** Latest payload passed to track(). Kept so we can re-track after a
   *  reconnect (Supabase fires SUBSCRIBED again on resume). Null while
   *  the local user hasn't joined the floor. */
  private trackedPayload: PresenceUser | null = null;
  /** Heartbeat interval id. Re-emits the current snapshot every few
   *  seconds so a dropped sync event self-heals. */
  private heartbeat: number | null = null;
  /** Re-track interval id. Periodically re-broadcasts our own presence
   *  row so subscribers that missed the original join (or were offline)
   *  receive a fresh presence event on the next tick. */
  private retrackBeat: number | null = null;

  constructor(roomId: string) {
    this.roomId = roomId;
    this.clientKey =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  /** Tell the channel which auth user is local so snapshots can filter
   *  self out of the remote list. Safe to call before or after start(). */
  setSelfUserId(userId: string | null): void {
    if (this.selfUserId === userId) return;
    this.selfUserId = userId;
    // The change can flip whether the current snapshot includes "us" —
    // re-emit so listeners pick it up.
    if (this.subscribed) this.emitSync();
  }

  /** Open the channel and wire presence listeners. Safe to call once
   *  at room boot. No-op when Supabase isn't configured. */
  start(): void {
    if (!supabase || this.channel) return;

    this.channel = supabase
      .channel(`room_presence:${this.roomId}`, {
        config: { presence: { key: this.clientKey } },
      })
      .on('presence', { event: 'sync' }, () => this.emitSync())
      .on('presence', { event: 'join' }, () => this.emitSync())
      .on('presence', { event: 'leave' }, () => this.emitSync())
      .subscribe(async (status, err) => {
        if (status === 'SUBSCRIBED') {
          this.subscribed = true;
          console.info(`[presence] subscribed to room_presence:${this.roomId}`);
          // Reconnect path: replay the most recent track so other clients
          // see us reappear without us having to explicitly re-track.
          if (this.trackedPayload) {
            const result = await this.channel?.track(this.trackedPayload);
            console.info(`[presence] re-track on SUBSCRIBED →`, result);
          }
          // Explicit first sync — don't wait for the next presence event.
          // Without this, a fast joiner whose own track() resolves before
          // the first sync arrives can sit on an empty remote list.
          this.emitSync();
          // Start the defensive heartbeat. Idempotent — if start() is
          // called twice (it shouldn't be, but be safe), clear the prior.
          this.startHeartbeat();
          this.startRetrack();
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          this.subscribed = false;
          console.warn(`[presence] subscription ${status}`, err);
        }
      });
  }

  /** Broadcast that the local user is on the floor. Subsequent calls
   *  replace the previous payload (e.g. user re-picks their avatar
   *  without leaving). Safe to call before SUBSCRIBED — payload is
   *  applied as soon as the channel comes up. */
  async track(payload: PresenceUser): Promise<void> {
    this.trackedPayload = payload;
    if (this.subscribed && this.channel) {
      const result = await this.channel.track(payload);
      // Surface Supabase's status return so a silent 'rate limited' /
      // 'error' / 'timed out' is visible during multi-user debugging
      // instead of getting swallowed by void track().
      console.info(`[presence] track(${payload.userId}) →`, result);
      // Re-emit so our own listeners see the freshly-tracked state on
      // the same tick instead of waiting for a server round-trip.
      this.emitSync();
    }
  }

  /** Drop the local user's presence row. Called on sign-out / leave. */
  async untrack(): Promise<void> {
    this.trackedPayload = null;
    if (this.subscribed && this.channel) {
      const result = await this.channel.untrack();
      console.info(`[presence] untrack →`, result);
      this.emitSync();
    }
  }

  onSync(cb: PresenceListener): () => void {
    this.listeners.add(cb);
    // Fire immediately with the current state so late subscribers don't
    // wait for the next presence event.
    if (this.subscribed) cb(this.snapshotRemoteUsers());
    return () => { this.listeners.delete(cb); };
  }

  stop(): void {
    this.stopHeartbeat();
    this.stopRetrack();
    if (this.channel && supabase) supabase.removeChannel(this.channel);
    this.channel = null;
    this.subscribed = false;
    this.listeners.clear();
    this.trackedPayload = null;
  }

  private emitSync(): void {
    const remote = this.snapshotRemoteUsers();
    // Always fire listeners — the dedup that used to live here meant a
    // heartbeat tick was skipped whenever the userId set was unchanged,
    // which made the listener miss state that depends on context outside
    // the user list (e.g. local self spectator flag, booth occupancy).
    // Listeners are idempotent and updatePopulation is a cheap textContent
    // write, so re-firing every 3s is essentially free and self-heals
    // any drift between the local cache and what listeners think is
    // current. Critically, it also covers the case where presenceState()
    // *did* update from a join event but the count text didn't refresh
    // because of an earlier render race.
    for (const l of this.listeners) l(remote);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = window.setInterval(() => {
      // Re-read state from the channel and emit. Safety net for missed
      // presence:join / presence:sync events.
      if (this.subscribed) this.emitSync();
    }, PRESENCE_HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  /** Re-broadcast our own presence row on a slower interval. This forces
   *  the server to fan a fresh presence:join event out to every other
   *  subscriber, so a tab that missed our original join (network blip,
   *  background-tab throttling, supabase realtime hiccup) sees us appear
   *  on the next ping. Only runs while we have a tracked payload — pure
   *  spectators don't generate traffic. */
  private startRetrack(): void {
    this.stopRetrack();
    this.retrackBeat = window.setInterval(() => {
      if (!this.subscribed || !this.channel || !this.trackedPayload) return;
      void this.channel.track(this.trackedPayload).catch((err) => {
        console.warn('[presence] retrack failed', err);
      });
    }, PRESENCE_RETRACK_MS);
  }

  private stopRetrack(): void {
    if (this.retrackBeat !== null) {
      clearInterval(this.retrackBeat);
      this.retrackBeat = null;
    }
  }

  /** Read presenceState() and collapse it into a deduped list of users,
   *  filtering out the local user (main.ts manages self separately). */
  private snapshotRemoteUsers(): PresenceUser[] {
    if (!this.channel) return [];
    const state = this.channel.presenceState<PresenceRow>();
    // Dedupe by userId — two tabs of the same human share one avatar.
    // Keyed-by-clientKey state can have multiple keys mapping to the
    // same userId payload.
    const byUser = new Map<string, PresenceUser>();
    for (const key of Object.keys(state)) {
      const rows = state[key];
      if (!rows || rows.length === 0) continue;
      const row = rows[0];
      if (!row.userId || !row.handle) continue;
      if (row.userId === this.selfUserId) continue;
      if (byUser.has(row.userId)) continue;
      byUser.set(row.userId, {
        userId: row.userId,
        handle: row.handle,
        avatar3dId: row.avatar3dId,
        avatarSeed: row.avatarSeed,
        avatarPaletteIndex: row.avatarPaletteIndex,
      });
    }
    return Array.from(byUser.values());
  }
}
